/**
 * Notification Sound Service
 *
 * Plays notification sounds for chat messages and other events.
 * Supports built-in presets + custom user-uploaded sounds.
 *
 * Sound preference is stored per-user in Firestore `userProfiles/{uid}.notificationSound`
 * and a platform default in `siteSettings/notifications.defaultSound`.
 */

import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db, storage } from '@/lib/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// ─── Built-in sound presets ─────────────────────────────────────────────────

export interface SoundPreset {
  id: string;
  label: string;
  url: string; // relative path under /sounds/
}

export const SOUND_PRESETS: SoundPreset[] = [
  { id: 'ding',    label: 'Ding',    url: '/sounds/ding.wav' },
  { id: 'pop',     label: 'Pop',     url: '/sounds/pop.wav' },
  { id: 'chime',   label: 'Chime',   url: '/sounds/chime.wav' },
  { id: 'bell',    label: 'Bell',    url: '/sounds/bell.wav' },
  { id: 'whoosh',  label: 'Whoosh',  url: '/sounds/whoosh.wav' },
  { id: 'gentle',  label: 'Gentle',  url: '/sounds/gentle.wav' },
];

export const DEFAULT_SOUND_ID = 'ding';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface NotificationSoundPrefs {
  enabled: boolean;
  soundId: string;          // preset id or 'custom'
  customSoundUrl?: string;  // Firebase Storage URL if custom
  volume: number;           // 0–100
  /** Older single choice (sound / voice / both) — read only, see `announceWays`. */
  announce?: 'sound' | 'voice' | 'both';
  /** How a new chat message is announced while BB is open — any mix of the
   *  sound, coπ saying it (in the person's coπ voice) and a vibration
   *  (Gordon 2026-11-01: "voice, sound, vibration, either or all at once"). */
  announceSound?: boolean;
  announceVoice?: boolean;
  announceVibrate?: boolean;
}

/** Which ways are on — the new switches, else the older single choice. */
export function announceWays(p: NotificationSoundPrefs): { sound: boolean; voice: boolean; vibrate: boolean } {
  const old = p.announce || 'sound';
  return {
    sound: p.announceSound ?? old !== 'voice',
    voice: p.announceVoice ?? old !== 'sound',
    vibrate: p.announceVibrate ?? false,
  };
}

export const DEFAULT_PREFS: NotificationSoundPrefs = {
  enabled: true,
  soundId: DEFAULT_SOUND_ID,
  volume: 70,
};

// ─── Audio cache (singleton) ────────────────────────────────────────────────

const audioCache = new Map<string, HTMLAudioElement>();

function getAudioUrl(prefs: NotificationSoundPrefs): string {
  if (prefs.soundId === 'custom' && prefs.customSoundUrl) {
    return prefs.customSoundUrl;
  }
  const preset = SOUND_PRESETS.find((p) => p.id === prefs.soundId);
  return preset?.url ?? SOUND_PRESETS[0].url;
}

/** Play the notification sound based on prefs. Returns immediately. */
export function playNotificationSound(prefs: NotificationSoundPrefs): void {
  if (!prefs.enabled || prefs.volume <= 0) return;

  const url = getAudioUrl(prefs);
  try {
    let audio = audioCache.get(url);
    if (!audio) {
      audio = new Audio(url);
      audioCache.set(url, audio);
    }
    audio.volume = Math.min(1, Math.max(0, prefs.volume / 100));
    audio.currentTime = 0;
    audio.play().catch(() => {
      // Browser may block autoplay until user interaction — ignore silently
    });
  } catch {
    // Fallback: if cached audio element is broken, create a fresh one
    const fresh = new Audio(url);
    fresh.volume = Math.min(1, Math.max(0, prefs.volume / 100));
    fresh.play().catch(() => {});
    audioCache.set(url, fresh);
  }
}

/** Preview a specific sound by preset id or custom URL. */
export function previewSound(soundId: string, customUrl?: string, volume = 70): void {
  const url = soundId === 'custom' && customUrl
    ? customUrl
    : SOUND_PRESETS.find((p) => p.id === soundId)?.url ?? SOUND_PRESETS[0].url;
  const audio = new Audio(url);
  audio.volume = Math.min(1, Math.max(0, volume / 100));
  audio.play().catch(() => {});
}

// ─── Firestore persistence ──────────────────────────────────────────────────

/** Get user's notification sound prefs (falls back to platform default, then built-in default). */
export async function getNotificationSoundPrefs(uid: string): Promise<NotificationSoundPrefs> {
  // 1. Check user-level pref
  const userSnap = await getDoc(doc(db, 'userProfiles', uid));
  const userData = userSnap.data();
  if (userData?.notificationSound) {
    return { ...DEFAULT_PREFS, ...userData.notificationSound };
  }

  // 2. Check platform default
  const siteSnap = await getDoc(doc(db, 'siteSettings', 'notifications'));
  const siteData = siteSnap.data();
  if (siteData?.defaultSound) {
    return { ...DEFAULT_PREFS, soundId: siteData.defaultSound };
  }

  return { ...DEFAULT_PREFS };
}

/** Save user's notification sound prefs. */
export async function saveNotificationSoundPrefs(uid: string, prefs: NotificationSoundPrefs): Promise<void> {
  // The app shell's new-message handler picks the change up at once.
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('bb-notif-prefs', { detail: prefs }));
  await setDoc(doc(db, 'userProfiles', uid), { notificationSound: prefs }, { merge: true });
}

/** Save platform default sound (Super Admin). */
export async function savePlatformDefaultSound(soundId: string): Promise<void> {
  await setDoc(doc(db, 'siteSettings', 'notifications'), {
    defaultSound: soundId,
    updatedAt: Timestamp.now(),
  }, { merge: true });
}

/** Get platform default sound id. */
export async function getPlatformDefaultSound(): Promise<string> {
  const snap = await getDoc(doc(db, 'siteSettings', 'notifications'));
  return snap.data()?.defaultSound ?? DEFAULT_SOUND_ID;
}

/** Upload a custom notification sound to Firebase Storage. Returns download URL. */
export async function uploadCustomSound(uid: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() || 'mp3';
  const path = `notification-sounds/${uid}/${Date.now()}.${ext}`;
  const fileRef = storageRef(storage, path);
  await uploadBytes(fileRef, file, { contentType: file.type });
  return getDownloadURL(fileRef);
}
